import Layout from '@/layout/Layout.vue'
import { P } from '@/config/permissions'

const adminRoutes = {
  path: '/administrator',
  component: Layout,
  name: 'Administrator',
  alwaysShow: true,
  meta: {
    title: 'Paramètres',
    icon: 'mdi:cog-outline',
    permissions: [
      P.ADMIN.MANAGE_PERMISSIONS,
      P.ADMIN.MANAGE_ROLES,
      P.USERS.BROWSE,
      P.AUDIT.BROWSE_LOGS,
      P.AUDIT_PROJETS.BROWSE,
      P.TICKETS.CONFIGURE,
      P.LINKS.BROWSE,
      P.NETWORK_CONFIG.MANAGE,
      P.NOTIFICATIONS.MANAGE,
    ],
  },
  children: [
    // Sous-menu Utilisateurs
    {
      path: 'users',
      // Pas de component : nœud de regroupement sidebar uniquement
      // Les enfants sont aplatis par permission.js avant ajout au router
      redirect: '/administrator/users',
      name: 'UsersMenu',
      alwaysShow: true,
      meta: {
        title: 'Utilisateurs',
        icon: 'mdi:account-group',
        // OR : visible si l'utilisateur a accès aux internes OU aux extranet
        permissions: [P.USERS.BROWSE, P.ADMIN.MANAGE_ROLES],
      },
      children: [
        // voir un utilisateur (hidden)
        {
          path: 'edit/:id(\\d+)',
          component: () => import('@/views/users/Profile'),
          name: 'UserProfile',
          // Pas de `noCache` : cet ecran recharge lui-meme ses donnees dans `activated()`,
          // hook qui ne se declenche que sous <keep-alive>. L'en sortir le laisse vide.
          meta: {
            title: 'Modification utilisateur',
            permissions: [P.USERS.BROWSE, P.USERS.EDIT],
          },
          hidden: true,
        },

        // Utilisateurs internes
        {
          path: '',
          component: () => import('@/views/users/Users'),
          name: 'UserList',
          // Pas de `noCache` : cet ecran recharge lui-meme ses donnees dans `activated()`,
          // hook qui ne se declenche que sous <keep-alive>. L'en sortir le laisse vide.
          meta: {
            title: 'Internes',
            icon: 'mdi:account-tie',
            permissions: [P.USERS.BROWSE],
          },
        },

        // Utilisateurs extranet
        {
          path: 'extranet',
          component: () => import('@/views/permission/ExtranetUsers'),
          name: 'ExtranetUsers',
          meta: {
            title: 'Extranet',
            noCache: true,
            icon: 'mdi:account-multiple-outline',
            permissions: [P.ADMIN.MANAGE_ROLES],
          },
        },
      ],
    },

    // Gestion des droits (rôles + permissions + matrice + tokens API)
    {
      path: 'permissions',
      component: () => import('@/views/permission/DashPermission'),
      name: 'DashPermission',
      // Pas de `noCache` : cet ecran recharge lui-meme ses donnees dans `activated()`,
      // hook qui ne se declenche que sous <keep-alive>. L'en sortir le laisse vide.
      meta: {
        title: 'Gestion des droits',
        icon: 'mdi:shield-lock-outline',
        permissions: [P.ADMIN.MANAGE_ROLES],
      },
    },

    // Journaux d'audit — un seul écran, deux vues. Ils répondent à la même question (qui a fait
    // quoi) sur deux sources : `activity_log` pour les comptes, factures et comptes SIP ; le
    // journal de la gestion de projet, filtrable par équipe, pour les tableaux. Une seule ligne
    // de menu au lieu de deux, l'onglet ouvert se lisant dans l'adresse.
    {
      path: 'audits',
      component: () => import('@/views/admin/AuditsDash.vue'),
      name: 'Audits',
      meta: {
        title: "Journaux d'audit",
        noCache: true,
        icon: 'mdi:history',
        permissions: [P.AUDIT.BROWSE_LOGS, P.AUDIT_PROJETS.BROWSE],
      },
    },

    // Anciennes adresses, gardées : elles circulent dans des signets et des liens de tickets.
    {
      path: 'audit',
      redirect: { name: 'Audits', query: { vue: 'general' } },
      name: 'AuditLog',
      hidden: true,
      meta: { permissions: [P.AUDIT.BROWSE_LOGS] },
    },
    {
      path: 'audit-projets',
      redirect: { name: 'Audits', query: { vue: 'projets' } },
      name: 'AuditProjets',
      hidden: true,
      meta: { permissions: [P.AUDIT_PROJETS.BROWSE] },
    },

    // Réglage du support — guichets, réponses types, consignes de l'assistant. Trois vues en une
    // ligne de menu : elles se règlent ensemble, et le menu des paramètres est déjà long.
    {
      path: 'support',
      component: () => import('@/views/admin/SupportReglages.vue'),
      name: 'SupportReglages',
      meta: {
        title: 'Support',
        noCache: true,
        icon: 'mdi:lifebuoy',
        permissions: [P.TICKETS.CONFIGURE],
      },
    },

    // Ancienne adresse, gardée : elle a circulé le temps d'une version.
    {
      path: 'guichets-support',
      redirect: { name: 'SupportReglages', query: { vue: 'guichets' } },
      name: 'GuichetsSupport',
      hidden: true,
      meta: { permissions: [P.TICKETS.CONFIGURE] },
    },

    // Liens d'accès
    {
      path: 'links',
      component: () => import('@/views/links/Links'),
      name: 'LinksDash',
      // Pas de `noCache` : cet ecran recharge lui-meme ses donnees dans `activated()`,
      // hook qui ne se declenche que sous <keep-alive>. L'en sortir le laisse vide.
      meta: {
        title: 'Liens d\'accès',
        icon: 'mdi:web',
        permissions: [P.LINKS.BROWSE, P.NETWORK_CONFIG.MANAGE],
      },
    },

    // Catalogue des codes d'erreur applicatifs (référence pour le support)
    {
      path: 'error-codes',
      component: () => import('@/views/admin/ErrorCodes.vue'),
      name: 'ErrorCodes',
      meta: {
        title: 'Codes d\'erreur',
        noCache: true,
        icon: 'mdi:alert-circle-outline',
        permissions: [P.ADMIN.MANAGE_ROLES],
      },
    },

    // Catalogue des types de notification
    {
      path: 'notification-types',
      component: () => import('@/views/admin/notifications/NotificationTypesAdmin.vue'),
      name: 'NotificationTypesAdmin',
      meta: {
        title: 'Notifications',
        noCache: true,
        icon: 'mdi:bell-cog-outline',
        permissions: [P.NOTIFICATIONS.MANAGE],
      },
    },
  ],
}

export default adminRoutes
